import DOMPurify from 'dompurify'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMemberStore } from '@/lib/store/member-store'
import { fetchMediaUrl } from '@/lib/utils/decrypt-attachment'
import { getClient } from '@/lib/matrix/client'
import { CollapsibleCode } from '@/components/timeline/CollapsibleCode'

// Matrix's permitted HTML subset (m.room.message rich replies / formatting).
// https://spec.matrix.org/latest/client-server-api/#mroommessage-msgtypes
const ALLOWED_TAGS = [
  'font', 'del', 's', 'strike', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
  'p', 'a', 'ul', 'ol', 'sup', 'sub', 'li', 'b', 'i', 'u', 'strong', 'em', 'code',
  'hr', 'br', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption', 'pre',
  'span', 'img', 'details', 'summary',
]

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height', 'start', 'color', 'name',
  'target', 'rel', 'class', 'data-mx-color', 'data-mx-bg-color', 'data-mx-spoiler',
  'data-mx-maths', 'data-mx-emoticon',
]

// Default DOMPurify URI allowlist, plus `mxc:` so bridged inline images/emotes survive.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel|callto|sms|cid|xmpp|mxc):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i

let hooksReady = false
function ensureHooks() {
  if (hooksReady) return
  // Drop the entire rich-reply fallback block — we render replies separately.
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'mx-reply') (node as Element).remove()
  })
  hooksReady = true
}

function sanitizeToFragment(html: string): DocumentFragment {
  ensureHooks()
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    RETURN_DOM_FRAGMENT: true,
  }) as unknown as DocumentFragment
}

interface MatrixToTarget {
  kind: 'user' | 'room'
  id: string
}

function parseMatrixTo(href: string): MatrixToTarget | null {
  const m = href.match(/^https?:\/\/matrix\.to\/#\/(.+)$/i)
  if (!m) return null
  // Fragment can be `@user:server`, `!room:server/$event`, `#alias:server`.
  const target = decodeURIComponent(m[1]!.split('?')[0]!.split('/')[0]!)
  if (target.startsWith('@')) return { kind: 'user', id: target }
  if (target.startsWith('!') || target.startsWith('#')) return { kind: 'room', id: target }
  return null
}

const EMPTY_MEMBERS: import('@/lib/store/member-store').MemberData[] = []

function localpart(userId: string): string {
  return userId.replace(/^@(.+?):.*$/, '$1')
}

// Strip a leading "@" and a trailing bridge tag like " (Telegram)" / " (@handle)".
function cleanName(name: string): string {
  return name
    .replace(/^@/, '')
    .replace(/\s*\((?:Telegram|WhatsApp|Signal|Discord|Slack|@[^)]+)\)\s*$/i, '')
    .trim()
}

// Profile-name cache for mentioned users who aren't members of the open room
// (e.g. a Telegram contact referenced in a message). Avoids refetching.
const profileNameCache = new Map<string, string | null>()

function UserPill({ userId, roomId, linkText }: { userId: string; roomId?: string; linkText?: string }) {
  const members = useMemberStore((s) => (roomId ? s.members.get(roomId) : undefined) ?? EMPTY_MEMBERS)
  const [fetched, setFetched] = useState<string | null>(() => profileNameCache.get(userId) ?? null)

  const memberName = members.find((m) => m.userId === userId)?.displayName
  // The anchor text is what the sender's client rendered for the mention
  // (e.g. a Telegram @username) — the most faithful label when we have it.
  const fromLink = linkText && cleanName(linkText) && cleanName(linkText) !== localpart(userId) ? cleanName(linkText) : ''
  const cachedProfile = getClient()?.getUser(userId)?.rawDisplayName
  const name = memberName || fromLink || cleanName(fetched || '') || cleanName(cachedProfile || '') || localpart(userId)

  useEffect(() => {
    // Only hit the network when we have nothing better than the bare id.
    if (memberName || fromLink || cachedProfile || profileNameCache.has(userId)) return
    const client = getClient()
    if (!client) return
    let active = true
    client
      .getProfileInfo(userId)
      .then((p) => {
        const n = p?.displayname ?? null
        profileNameCache.set(userId, n)
        if (active && n) setFetched(n)
      })
      .catch(() => profileNameCache.set(userId, null))
    return () => {
      active = false
    }
  }, [userId, memberName, fromLink, cachedProfile])

  return (
    <span
      title={userId}
      className="inline rounded px-1 font-medium text-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.12)]"
    >
      @{name}
    </span>
  )
}

function RoomPill({ children, label }: { children: ReactNode; label: string }) {
  return (
    <span
      title={label}
      className="inline rounded px-1 font-medium text-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.12)]"
    >
      {children}
    </span>
  )
}

function Spoiler({ reason, children }: { reason: string | null; children: ReactNode }) {
  const [revealed, setRevealed] = useState(false)
  return (
    <span
      onClick={() => setRevealed(true)}
      title={!revealed ? reason || 'Spoiler' : undefined}
      className={
        revealed
          ? ''
          : 'rounded bg-[rgb(var(--np-tx-primary))] text-transparent cursor-pointer select-none [filter:blur(0.3em)]'
      }
    >
      {children}
    </span>
  )
}

// Inline images / custom emoji arrive as mxc: URIs; fetch them authenticated as
// blobs (same path as attachments) rather than letting the browser hit Synapse
// without a token. Remote http(s) images are dropped for privacy/security.
function InlineMxcImage({ src, alt, emoticon }: { src: string; alt: string; emoticon: boolean }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    fetchMediaUrl(src).then((u) => active && setUrl(u)).catch(() => {})
    return () => {
      active = false
    }
  }, [src])

  if (!url) return emoticon ? <span>{alt || '…'}</span> : null
  return (
    <img
      src={url}
      alt={alt}
      className={emoticon ? 'inline-block h-5 align-text-bottom' : 'max-w-xs rounded-md my-1'}
    />
  )
}

// Structural / inline tags rendered 1:1 with light styling.
const PLAIN_TAGS: Record<string, { tag: string; className?: string }> = {
  p: { tag: 'p', className: 'my-1 first:mt-0 last:mb-0' },
  div: { tag: 'div' },
  strong: { tag: 'strong', className: 'font-semibold' },
  b: { tag: 'strong', className: 'font-semibold' },
  em: { tag: 'em' },
  i: { tag: 'em' },
  u: { tag: 'u' },
  del: { tag: 'del' },
  s: { tag: 'del' },
  strike: { tag: 'del' },
  sup: { tag: 'sup' },
  sub: { tag: 'sub' },
  br: { tag: 'br' },
  hr: { tag: 'hr', className: 'my-2 border-[rgb(var(--np-border))]' },
  ul: { tag: 'ul', className: 'list-disc pl-5 my-1' },
  ol: { tag: 'ol', className: 'list-decimal pl-5 my-1' },
  li: { tag: 'li' },
  h1: { tag: 'h1', className: 'text-base font-semibold my-1' },
  h2: { tag: 'h2', className: 'text-base font-semibold my-1' },
  h3: { tag: 'h3', className: 'text-sm font-semibold my-1' },
  h4: { tag: 'h4', className: 'text-sm font-semibold my-1' },
  h5: { tag: 'h5', className: 'text-sm font-semibold my-1' },
  h6: { tag: 'h6', className: 'text-sm font-semibold my-1' },
  table: { tag: 'table', className: 'my-1 border-collapse text-xs' },
  thead: { tag: 'thead' },
  tbody: { tag: 'tbody' },
  tr: { tag: 'tr' },
  th: { tag: 'th', className: 'border border-[rgb(var(--np-border))] px-2 py-0.5 text-left' },
  td: { tag: 'td', className: 'border border-[rgb(var(--np-border))] px-2 py-0.5' },
  caption: { tag: 'caption' },
  details: { tag: 'details' },
  summary: { tag: 'summary', className: 'cursor-pointer' },
}

function colorStyle(el: Element): React.CSSProperties | undefined {
  const fg = el.getAttribute('data-mx-color') || el.getAttribute('color')
  const bg = el.getAttribute('data-mx-bg-color')
  if (!fg && !bg) return undefined
  const style: React.CSSProperties = {}
  // Constrain to hex colors to avoid CSS injection via the style attribute.
  if (fg && /^#[0-9a-f]{3,8}$/i.test(fg)) style.color = fg
  if (bg && /^#[0-9a-f]{3,8}$/i.test(bg)) style.backgroundColor = bg
  return Object.keys(style).length ? style : undefined
}

function childrenToReact(parent: ParentNode, roomId?: string): ReactNode[] {
  return Array.from(parent.childNodes).map((child, i) => nodeToReact(child, i, roomId))
}

function nodeToReact(node: Node, key: number, roomId?: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent
  if (node.nodeType !== Node.ELEMENT_NODE) return null

  const el = node as Element
  const tag = el.tagName.toLowerCase()
  const kids = childrenToReact(el, roomId)

  switch (tag) {
    case 'a': {
      const href = el.getAttribute('href') || ''
      const mx = parseMatrixTo(href)
      if (mx?.kind === 'user')
        return <UserPill key={key} userId={mx.id} roomId={roomId} linkText={el.textContent ?? undefined} />

      if (mx?.kind === 'room')
        return (
          <RoomPill key={key} label={mx.id}>
            {kids.length ? kids : mx.id}
          </RoomPill>
        )
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[rgb(var(--np-accent))] hover:underline break-all"
        >
          {kids}
        </a>
      )
    }
    case 'img': {
      const src = el.getAttribute('src') || ''
      if (!src.startsWith('mxc://')) return null
      return (
        <InlineMxcImage
          key={key}
          src={src}
          alt={el.getAttribute('alt') || ''}
          emoticon={el.hasAttribute('data-mx-emoticon')}
        />
      )
    }
    case 'span':
    case 'font': {
      if (el.hasAttribute('data-mx-spoiler')) {
        return (
          <Spoiler key={key} reason={el.getAttribute('data-mx-spoiler')}>
            {kids}
          </Spoiler>
        )
      }
      const style = colorStyle(el)
      return (
        <span key={key} style={style}>
          {kids}
        </span>
      )
    }
    case 'blockquote': {
      // mautrix-telegram marks the quoted body of a forwarded message with this
      // attribute; give it a clear "Forwarded" affordance.
      const forwarded = el.hasAttribute('data-telegram-forward')
      return (
        <blockquote
          key={key}
          className={`border-l-2 pl-2 my-1 text-[rgb(var(--np-tx-secondary))] ${
            forwarded ? 'border-[rgb(var(--np-accent)/0.6)]' : 'border-[rgb(var(--np-border))]'
          }`}
        >
          {forwarded && (
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--np-accent))] mb-0.5">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 17 20 12 15 7" />
                <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
              </svg>
              Forwarded
            </span>
          )}
          {kids}
        </blockquote>
      )
    }
    case 'code':
      return (
        <code
          key={key}
          className="px-1 py-0.5 rounded text-xs font-data bg-[rgb(var(--np-raised))] text-[rgb(var(--np-accent))]"
        >
          {kids}
        </code>
      )
    case 'pre':
      return <CollapsibleCode key={key} text={el.textContent ?? ''}>{kids}</CollapsibleCode>
    default: {
      const mapped = PLAIN_TAGS[tag]
      if (!mapped) return <span key={key}>{kids}</span>
      if (mapped.tag === 'br') return <br key={key} />
      if (mapped.tag === 'hr') return <hr key={key} className={mapped.className} />
      const Tag = mapped.tag as keyof React.JSX.IntrinsicElements
      return (
        <Tag key={key} className={mapped.className}>
          {kids}
        </Tag>
      )
    }
  }
}

export function MatrixHtml({ html, roomId }: { html: string; roomId?: string }) {
  const nodes = useMemo(() => childrenToReact(sanitizeToFragment(html), roomId), [html, roomId])
  // HTML controls its own whitespace via tags; override the parent's pre-wrap.
  return <span className="whitespace-normal break-words">{nodes}</span>
}
